import { useId, useMemo, useState, type FC, type FormEvent } from 'react';
import type { ShipmentDetailsDto } from '@/api';
import {
  Alert,
  Button,
  Combobox,
  Dialog,
  Input,
  Select,
  Textarea,
  useDebouncedValue,
} from '@/ui';

import {
  resolveApiErrorMessage,
  resolveApiValidationMessages,
} from '../../lib/apiError';
import { formatInstantDate, toNumber } from '../../lib/format';
import {
  useAvailableStock,
  useDeliveryAddressHint,
  useLastContract,
  useLastPrices,
  useLastWarehouse,
  usePartnerContracts,
  usePartnerCounterparties,
  usePartnerOptions,
  useProductOptions,
  useWarehouses,
} from './api/lookups';
import { useCreateShipment, useUpdateShipment } from './api/mutations';
import {
  DELIVERY_ADDRESS_MAX_LENGTH,
  draftFromShipment,
  emptyDraft,
  resolveContractId,
  resolveDeliveryAddress,
  resolveWarehouseId,
  toRequestLines,
  validateDraft,
  type PartnerOption,
  type ShipmentDraft,
} from './lib/draft';
import { PositionsEditor } from './PositionsEditor';

/*
 * Создание и правка отгрузки - PlanEditModal макета: клиент, плановая дата,
 * позиции. Отличия от макета - по решениям плана plan/shipment-onec-order:
 *   - клиент ищется по API, а не выбирается из короткого списка;
 *   - контрагент не выбирается: сервер подставит единственного действующего,
 *     а форма его только показывает;
 *   - склад отправки выбирает менеджер (в макете он есть только в карточке);
 *     пока не выбрал - подставлен склад прошлого заказа клиента;
 *   - договор клиента рядом со складом - как в 1С при создании заказа
 *     (plan/counterparty-contracts); пока менеджер не выбрал - подставлен договор
 *     прошлого заказа клиента с этого склада, прошлого заказа или единственный.
 *     Договор без банковского счёта выбрать можно, но передать в 1С - нет;
 *   - адрес доставки так же: пока менеджер поле не трогал - адрес последнего
 *     заказа клиента, без него - адрес клиента из 1С;
 *   - у позиции есть цена без НДС (её ставит менеджер) и подсказка прошлой цены.
 *
 * Окно монтируется только открытым (родитель рендерит его по условию), поэтому
 * черновик берётся из начального состояния и сбрасывается сам при закрытии.
 */

export interface ShipmentEditorDialogProps {
  /** Отгрузка для правки; без неё - создание новой. */
  shipment?: ShipmentDetailsDto;
  onClose: () => void;
  onSaved?: (shipment: ShipmentDetailsDto) => void;
}

const SEARCH_DEBOUNCE_MS = 250;

export const ShipmentEditorDialog: FC<ShipmentEditorDialogProps> = ({
  shipment,
  onClose,
  onSaved,
}) => {
  const formId = useId();
  const partnerFieldId = useId();
  const dateFieldId = useId();
  const warehouseFieldId = useId();
  const contractFieldId = useId();
  const addressFieldId = useId();
  const commentFieldId = useId();

  const [draft, setDraft] = useState<ShipmentDraft>(() =>
    shipment === undefined ? emptyDraft() : draftFromShipment(shipment),
  );
  const [problems, setProblems] = useState<ReadonlyArray<string>>([]);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');

  const partners = usePartnerOptions(
    useDebouncedValue(partnerQuery, SEARCH_DEBOUNCE_MS),
  );
  const products = useProductOptions(
    useDebouncedValue(productQuery, SEARCH_DEBOUNCE_MS),
  );
  const counterparties = usePartnerCounterparties(draft.partner?.id);
  const warehouses = useWarehouses();
  const lastWarehouse = useLastWarehouse(draft.partner?.id);
  const hint = lastWarehouse.data;
  const warehouseId = resolveWarehouseId(draft, hint?.warehouseId);
  const isHinted = draft.warehouseId === null && warehouseId !== '';
  const contracts = usePartnerContracts(draft.partner?.id);
  const lastContract = useLastContract(
    draft.partner?.id,
    warehouseId === '' ? undefined : warehouseId,
  );
  const contractHint = lastContract.data;
  const contractId = resolveContractId(draft, contractHint?.contractId);
  const isContractHinted = draft.contractId === null && contractId !== '';
  const contract = (contracts.data ?? []).find(
    (item) => item.id === contractId,
  );
  const addressHint = useDeliveryAddressHint(draft.partner?.id).data;
  const deliveryAddress = resolveDeliveryAddress(draft, addressHint?.address);
  const isAddressHinted =
    draft.deliveryAddress === null && deliveryAddress !== '';

  const productIds = useMemo(
    () =>
      draft.lines.flatMap((line) =>
        line.product === null ? [] : [line.product.id],
      ),
    [draft.lines],
  );
  const lastPrices = useLastPrices(draft.partner?.id, productIds);
  const lastPriceByProduct = useMemo(
    () =>
      new Map((lastPrices.data ?? []).map((price) => [price.productId, price])),
    [lastPrices.data],
  );
  const availableStock = useAvailableStock(
    warehouseId === '' ? undefined : warehouseId,
    productIds,
  );
  const availableByProduct = useMemo(
    () =>
      new Map(
        (availableStock.data ?? []).map((stock) => [
          stock.productId,
          stock.availableQuantity == null
            ? null
            : toNumber(stock.availableQuantity),
        ]),
      ),
    [availableStock.data],
  );

  const create = useCreateShipment();
  const update = useUpdateShipment();
  const mutation = shipment === undefined ? create : update;

  const partnerOptions: ReadonlyArray<PartnerOption> = (
    partners.data?.items ?? []
  ).map((partner) => ({
    id: partner.id,
    name: partner.name,
    code: partner.code,
  }));

  const productOptions = (products.data?.items ?? []).map((product) => ({
    id: product.id,
    code: product.code,
    name: product.name,
    unitName: product.unitName,
  }));

  const serverMessages = mutation.isError
    ? resolveApiValidationMessages(mutation.error)
    : [];
  const serverError =
    mutation.isError && serverMessages.length === 0
      ? resolveApiErrorMessage(mutation.error)
      : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const found = validateDraft(draft);
    setProblems(found);
    if (found.length > 0 || draft.partner === null) return;

    const body = {
      partnerId: draft.partner.id,
      plannedDate: draft.plannedDate,
      warehouseId: warehouseId === '' ? null : warehouseId,
      contractId: contractId === '' ? null : contractId,
      deliveryAddress:
        deliveryAddress.trim() === '' ? null : deliveryAddress.trim(),
      comment: draft.comment.trim() === '' ? null : draft.comment.trim(),
      lines: toRequestLines(draft),
    };

    const handlers = {
      onSuccess: (saved: ShipmentDetailsDto) => {
        onSaved?.(saved);
        onClose();
      },
    };

    if (shipment === undefined) create.mutate({ body }, handlers);
    else update.mutate({ path: { id: shipment.id }, body }, handlers);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      widthClassName="w-[min(760px,94vw)]"
      title={
        shipment === undefined
          ? 'Новая отгрузка'
          : `Отгрузка № ${shipment.number}: правка`
      }
      description="В одну отгрузку можно добавить несколько позиций. Контрагент подставляется из карточки клиента."
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Сохраняем...' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-3.5">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={partnerFieldId}
              className="text-xs font-medium text-ink-700"
            >
              Клиент
            </label>
            <Combobox
              id={partnerFieldId}
              value={draft.partner}
              onChange={(partner) =>
                setDraft({ ...draft, partner, contractId: null })
              }
              onQueryChange={setPartnerQuery}
              options={partnerOptions}
              isLoading={partners.isFetching}
              getKey={(partner) => partner.id}
              getLabel={(partner) => partner.name}
              renderOption={(partner) => (
                <span className="flex items-baseline justify-between gap-3">
                  <span className="truncate">{partner.name}</span>
                  <span className="shrink-0 font-mono text-[11px] text-ink-500">
                    {partner.code}
                  </span>
                </span>
              )}
              placeholder="Найти по названию или коду"
              emptyLabel="Клиент не найден"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={dateFieldId}
              className="text-xs font-medium text-ink-700"
            >
              Плановая дата
            </label>
            <Input
              id={dateFieldId}
              type="date"
              value={draft.plannedDate}
              onChange={(event) =>
                setDraft({ ...draft, plannedDate: event.target.value })
              }
            />
          </div>

          <div className="col-span-2">
            <CounterpartyNote
              hasPartner={draft.partner !== null}
              isLoading={counterparties.isPending}
              counterparties={counterparties.data ?? []}
            />
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-3.5">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={warehouseFieldId}
                className="text-xs font-medium text-ink-700"
              >
                Склад отправки
              </label>
              <Select
                id={warehouseFieldId}
                value={warehouseId}
                onChange={(event) =>
                  setDraft({ ...draft, warehouseId: event.target.value })
                }
              >
                <option value="">Не выбран</option>
                {(warehouses.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </Select>
              {isHinted && hint?.orderNumber && hint.orderDate && (
                <span className="text-[12px] text-ink-500">
                  Как в прошлом заказе клиента № {hint.orderNumber} от{' '}
                  {formatInstantDate(hint.orderDate)}
                </span>
              )}
              {warehouseId === '' && (
                <span className="text-[12px] text-ink-500">
                  Без склада отгрузку можно сохранить, но передать в 1С не
                  получится.
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={contractFieldId}
                className="text-xs font-medium text-ink-700"
              >
                Договор
              </label>
              <Select
                id={contractFieldId}
                value={contractId}
                disabled={draft.partner === null}
                onChange={(event) =>
                  setDraft({ ...draft, contractId: event.target.value })
                }
              >
                <option value="">Не выбран</option>
                {(contracts.data ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.hasBankAccount
                      ? item.name
                      : `${item.name} - нет банковского счёта`}
                  </option>
                ))}
              </Select>
              <ContractNote
                hasPartner={draft.partner !== null}
                isLoading={
                  contracts.isPending ||
                  (draft.contractId === null && lastContract.isFetching)
                }
                contractCount={contracts.data?.length ?? 0}
                isSelected={contractId !== ''}
                hasBankAccount={contract?.hasBankAccount ?? true}
                hint={isContractHinted ? contractHint : undefined}
              />
            </div>
          </div>

          <div className="col-span-2 flex flex-col gap-1.5">
            <label
              htmlFor={addressFieldId}
              className="text-xs font-medium text-ink-700"
            >
              Адрес доставки
            </label>
            <Textarea
              id={addressFieldId}
              rows={2}
              maxLength={DELIVERY_ADDRESS_MAX_LENGTH}
              placeholder="Куда везти - адрес уйдёт в заказ 1С"
              value={deliveryAddress}
              onChange={(event) =>
                setDraft({ ...draft, deliveryAddress: event.target.value })
              }
            />
            {isAddressHinted && addressHint && (
              <span className="text-[12px] text-ink-500">
                {addressHint.orderNumber && addressHint.orderDate
                  ? `Как в прошлом заказе клиента № ${addressHint.orderNumber} от ${formatInstantDate(addressHint.orderDate)}`
                  : 'Адрес клиента из 1С'}
              </span>
            )}
          </div>

          <div className="col-span-2 flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-700">
              Позиции отгрузки
            </span>
            <PositionsEditor
              lines={draft.lines}
              onChange={(lines) => setDraft({ ...draft, lines })}
              productOptions={productOptions}
              isProductsLoading={products.isFetching}
              onProductQueryChange={setProductQuery}
              lastPrices={lastPriceByProduct}
              availableStock={availableByProduct}
              plannedDate={draft.plannedDate}
            />
          </div>

          <div className="col-span-2 flex flex-col gap-1.5">
            <label
              htmlFor={commentFieldId}
              className="text-xs font-medium text-ink-700"
            >
              Комментарий
            </label>
            <Textarea
              id={commentFieldId}
              rows={2}
              placeholder="Договорённости с клиентом, окно разгрузки..."
              value={draft.comment}
              onChange={(event) =>
                setDraft({ ...draft, comment: event.target.value })
              }
            />
          </div>

          {(problems.length > 0 ||
            serverMessages.length > 0 ||
            serverError !== undefined) && (
            <Alert tone="error" className="col-span-2">
              {serverError ?? (
                <ul className="m-0 list-none p-0">
                  {[...problems, ...serverMessages].map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </div>
      </form>
    </Dialog>
  );
};

interface ContractNoteProps {
  hasPartner: boolean;
  isLoading: boolean;
  contractCount: number;
  isSelected: boolean;
  /** У выбранного договора есть банковский счёт; без договора - true. */
  hasBankAccount: boolean;
  /** Подсказка, если договор подставлен ею, а не выбран менеджером. */
  hint?: {
    orderNumber?: string | null;
    orderDate?: string | null;
    isFromSameWarehouse?: boolean;
  };
}

/** Под договором: откуда он подставлен и что помешает передать отгрузку в 1С. */
const ContractNote: FC<ContractNoteProps> = ({
  hasPartner,
  isLoading,
  contractCount,
  isSelected,
  hasBankAccount,
  hint,
}) => {
  if (!hasPartner) return null;

  // Подсказка пересчитывается при смене склада: «без договора» в этот момент было бы неправдой.
  if (isLoading)
    return (
      <span className="text-[12px] text-ink-500">
        Подбираем договор по прошлым заказам...
      </span>
    );

  if (contractCount === 0)
    return (
      <span className="text-[12px] text-st-approval">
        У клиента нет действующих договоров в 1С - передать в 1С не получится.
      </span>
    );

  if (!isSelected)
    return (
      <span className="text-[12px] text-ink-500">
        Без договора отгрузку можно сохранить, но передать в 1С не получится.
      </span>
    );

  if (!hasBankAccount)
    return (
      <span className="text-[12px] text-st-approval">
        В договоре нет банковского счёта организации - передать в 1С не
        получится, пока бухгалтер не заполнит счёт в договоре.
      </span>
    );

  if (hint === undefined) return null;

  if (hint.orderNumber && hint.orderDate)
    return (
      <span className="text-[12px] text-ink-500">
        Как в прошлом заказе клиента № {hint.orderNumber} от{' '}
        {formatInstantDate(hint.orderDate)}
        {hint.isFromSameWarehouse ? ' с этого склада' : ''}
      </span>
    );

  return (
    <span className="text-[12px] text-ink-500">
      Единственный договор клиента
    </span>
  );
};

interface CounterpartyNoteProps {
  hasPartner: boolean;
  isLoading: boolean;
  counterparties: ReadonlyArray<{
    id: string;
    name: string;
    inn?: string | null;
    kpp?: string | null;
  }>;
}

/** Юрлицо заказа: сервер подставит единственного действующего контрагента клиента. */
const CounterpartyNote: FC<CounterpartyNoteProps> = ({
  hasPartner,
  isLoading,
  counterparties,
}) => {
  if (!hasPartner) return null;

  if (isLoading)
    return (
      <div className="text-[12.5px] text-ink-500">Ищем контрагента...</div>
    );

  const [only] = counterparties;

  if (counterparties.length === 1 && only !== undefined)
    return (
      <div className="text-[12.5px] text-ink-600">
        Контрагент: <b className="font-medium text-ink-900">{only.name}</b>
        {only.inn ? ` · ИНН ${only.inn}` : ''}
        {only.kpp ? ` · КПП ${only.kpp}` : ''}
      </div>
    );

  return (
    <Alert tone="warn">
      {counterparties.length === 0
        ? 'У клиента нет действующего контрагента: отгрузку можно сохранить, но передать в 1С не получится.'
        : 'У клиента несколько контрагентов: контрагент не будет подставлен, и передать отгрузку в 1С не получится.'}
    </Alert>
  );
};
